import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

type FieldType = "text" | "textarea" | "date" | "url" | "boolean" | "number" | "json";

interface Field {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  default?: any;
}

interface Props {
  table: "daily_challenges" | "weekly_story" | "printable_friday" | "weekly_quizzes";
  title: string;
  fields: Field[];
  orderBy: string;
}

const RetentionManager = ({ table, title, fields, orderBy }: Props) => {
  const [rows, setRows] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);

  const load = async () => {
    const { data } = await supabase.from(table).select("*").order(orderBy, { ascending: false });
    setRows(data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const blank = () => {
    const o: any = {};
    fields.forEach((f) => (o[f.key] = f.default ?? (f.type === "boolean" ? true : "")));
    return o;
  };

  const save = async () => {
    const payload = { ...editing };
    fields.forEach((f) => {
      if (f.type === "json" && typeof payload[f.key] === "string") {
        try {
          payload[f.key] = JSON.parse(payload[f.key] || "[]");
        } catch {
          toast.error(`Invalid JSON in ${f.label}`);
          throw new Error("invalid json");
        }
      }
      if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
    });
    try {
      const { error } = payload.id
        ? await supabase.from(table).update(payload).eq("id", payload.id)
        : await supabase.from(table).insert(payload);
      if (error) throw error;
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e: any) {
      toast.error(e.message || "Save failed");
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this entry?")) return;
    await supabase.from(table).delete().eq("id", id);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">{title}</h2>
        <Button onClick={() => setEditing(blank())}>
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {editing && (
        <Card className="p-4 mb-4 space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-sm font-semibold block mb-1">{f.label}</label>
              {f.type === "textarea" || f.type === "json" ? (
                <Textarea
                  value={
                    f.type === "json" && typeof editing[f.key] !== "string"
                      ? JSON.stringify(editing[f.key] ?? [], null, 2)
                      : editing[f.key] ?? ""
                  }
                  onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                  rows={f.type === "json" ? 8 : 3}
                />
              ) : f.type === "boolean" ? (
                <input
                  type="checkbox"
                  checked={!!editing[f.key]}
                  onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })}
                />
              ) : (
                <Input
                  type={f.type === "date" ? "date" : f.type === "number" ? "number" : "text"}
                  value={editing[f.key] ?? ""}
                  onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <Button onClick={save}>Save</Button>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <Card key={r.id} className="p-3 flex items-center justify-between">
            <div>
              <div className="font-bold">{r.title}</div>
              <div className="text-xs text-gray-500">
                {r[orderBy]} {r.is_active ? "· active" : "· inactive"}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(r)}>
                Edit
              </Button>
              <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
        {rows.length === 0 && <p className="text-gray-500">No entries yet.</p>}
      </div>
    </div>
  );
};

export const DailyChallengesManager = () => (
  <RetentionManager
    table="daily_challenges"
    title="Daily Challenges"
    orderBy="challenge_date"
    fields={[
      { key: "challenge_date", label: "Date", type: "date", required: true },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "emoji", label: "Emoji", type: "text", default: "⚡" },
      { key: "link_url", label: "Link URL", type: "text" },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ]}
  />
);

export const WeeklyStoryManager = () => (
  <RetentionManager
    table="weekly_story"
    title="Story of the Week"
    orderBy="week_start"
    fields={[
      { key: "week_start", label: "Week Start (Monday)", type: "date", required: true },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "cover_image_url", label: "Cover Image URL", type: "text" },
      { key: "story_link", label: "Story Link", type: "text", required: true },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ]}
  />
);

export const PrintableFridayManager = () => (
  <RetentionManager
    table="printable_friday"
    title="Printable Friday"
    orderBy="week_start"
    fields={[
      { key: "week_start", label: "Week Start", type: "date", required: true },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "cover_image_url", label: "Cover Image URL", type: "text" },
      { key: "download_url", label: "Download URL (zip)", type: "text", required: true },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ]}
  />
);

export const WeeklyQuizzesManager = () => (
  <RetentionManager
    table="weekly_quizzes"
    title="Weekly Quizzes"
    orderBy="week_start"
    fields={[
      { key: "week_start", label: "Week Start", type: "date", required: true },
      { key: "title", label: "Title", type: "text", required: true },
      { key: "description", label: "Description", type: "textarea" },
      { key: "badge_name", label: "Badge Name", type: "text", default: "Quiz Champ" },
      { key: "badge_emoji", label: "Badge Emoji", type: "text", default: "🏆" },
      { key: "pass_score", label: "Pass Score (%)", type: "number", default: 70 },
      {
        key: "questions",
        label: 'Questions JSON: [{"question":"...","options":["A","B"],"correct":0}]',
        type: "json",
        default: [],
      },
      { key: "is_active", label: "Active", type: "boolean", default: true },
    ]}
  />
);

export default RetentionManager;
